import { Unit } from './types';

export const EPS_VOCAB: Unit = {
  id: 'eps-vocab',
  number: 4,
  title: 'EPS Vocabulary',
  description: 'The work words the test is full of: numbers, time, the workplace, tools, machines and safety.',
  emoji: '🏭',
  lessons: [
    {
      id: 'numbers-time',
      title: 'Numbers & time',
      summary: 'Count, read prices and tell the time',
      exercises: [
        { kind: 'teach', ko: '일 · 이 · 삼 · 사 · 오', speak: '일, 이, 삼, 사, 오', roman: 'il · i · sam · sa · o', en: '1 · 2 · 3 · 4 · 5', note: 'These numbers are used for money, dates, minutes and phone numbers.' },
        { kind: 'teach', ko: '십 · 백 · 천 · 만', speak: '십, 백, 천, 만', roman: 'sip · baek · cheon · man', en: '10 · 100 · 1,000 · 10,000' },
        { kind: 'teach', ko: '천 원', roman: 'cheon won', en: '1,000 won', picture: '💵', note: '원 (won) is Korean money. 오천 원 is 5,000 won.' },
        { kind: 'teach', ko: '세 시', roman: 'se si', en: "3 o'clock", picture: '🕒', note: 'Hours use different numbers: 한 시 (1), 두 시 (2), 세 시 (3), 네 시 (4).' },
        { kind: 'teach', ko: '삼십 분', roman: 'sam-sip bun', en: '30 minutes', note: 'Minutes use 일, 이, 삼 … again. 두 시 삼십 분 is 2:30.' },
        { kind: 'choice', prompt: 'Listen. How much is it?', speak: '오천 원', listenOnly: true, options: ['₩5,000', '₩500', '₩50,000', '₩1,500'], answer: 0, explain: '오천 원: 오 (5) × 천 (1,000).' },
        { kind: 'choice', prompt: 'What time is it?', ko: '세 시', options: ['3:00', '4:00', '10:00', '1:00'], answer: 0 },
        { kind: 'choice', prompt: 'Which number is 십?', ko: '십', options: ['10', '100', '1,000', '4'], answer: 0 },
        { kind: 'match', prompt: 'Match the number', pairs: [['삼', '3'], ['오', '5'], ['백', '100'], ['만', '10,000']] },
      ],
    },
    {
      id: 'workplace',
      title: 'Workplace',
      summary: 'Company, factory, office, dorm and pay',
      exercises: [
        { kind: 'teach', ko: '회사', roman: 'hoe-sa', en: 'company', picture: '🏢' },
        { kind: 'teach', ko: '공장', roman: 'gong-jang', en: 'factory', picture: '🏭' },
        { kind: 'teach', ko: '사무실', roman: 'sa-mu-sil', en: 'office' },
        { kind: 'teach', ko: '기숙사', roman: 'gi-suk-sa', en: 'dormitory', picture: '🛏️', note: 'Many EPS workers live in the company dorm.' },
        { kind: 'teach', ko: '월급', roman: 'wol-geup', en: 'monthly pay', picture: '💰' },
        { kind: 'teach', ko: '휴가', roman: 'hyu-ga', en: 'holiday, time off', picture: '🏖️' },
        { kind: 'choice', prompt: 'Listen. Which picture matches?', speak: '공장', listenOnly: true, options: ['🏭', '🏖️', '🛏️', '💰'], pictureOptions: true, answer: 0, explain: '공장 (gong-jang) is a factory.' },
        { kind: 'choice', prompt: 'Where do you sleep after work?', options: ['기숙사', '사무실', '공장', '월급'], answer: 0 },
        { kind: 'match', prompt: 'Match the word to its meaning', pairs: [['회사', 'company'], ['사무실', 'office'], ['월급', 'monthly pay'], ['휴가', 'time off']] },
      ],
    },
    {
      id: 'tools',
      title: 'Tools',
      summary: 'Hammer, scissors, knife, ladder and gloves',
      exercises: [
        { kind: 'teach', ko: '망치', roman: 'mang-chi', en: 'hammer', picture: '🔨' },
        { kind: 'teach', ko: '가위', roman: 'ga-wi', en: 'scissors', picture: '✂️' },
        { kind: 'teach', ko: '칼', roman: 'kal', en: 'knife', picture: '🔪' },
        { kind: 'teach', ko: '사다리', roman: 'sa-da-ri', en: 'ladder', picture: '🪜' },
        { kind: 'teach', ko: '장갑', roman: 'jang-gap', en: 'gloves', picture: '🧤' },
        { kind: 'teach', ko: '상자', roman: 'sang-ja', en: 'box', picture: '📦' },
        { kind: 'choice', prompt: 'Which picture is 망치?', ko: '망치', options: ['🔨', '✂️', '🪜', '🧤'], pictureOptions: true, answer: 0 },
        { kind: 'choice', prompt: 'Listen. Which picture matches?', speak: '가위', listenOnly: true, options: ['🔪', '✂️', '📦', '🔨'], pictureOptions: true, answer: 1, explain: '가위 (ga-wi) is scissors.' },
        { kind: 'choice', prompt: 'What is this?', picture: '🧤', options: ['장갑', '상자', '칼', '사다리'], answer: 0 },
        { kind: 'match', prompt: 'Match the tool', pairs: [['칼', 'knife'], ['사다리', 'ladder'], ['상자', 'box'], ['가위', 'scissors']] },
      ],
    },
    {
      id: 'machines',
      title: 'Machines',
      summary: 'Machine, power, button, on, off and broken',
      exercises: [
        { kind: 'teach', ko: '기계', roman: 'gi-gye', en: 'machine', picture: '⚙️' },
        { kind: 'teach', ko: '전원', roman: 'jeon-won', en: 'power (supply)', picture: '🔌' },
        { kind: 'teach', ko: '버튼', roman: 'beo-teun', en: 'button', picture: '🔘' },
        { kind: 'teach', ko: '켜요', roman: 'kyeo-yo', en: 'turn on' },
        { kind: 'teach', ko: '꺼요', roman: 'kkeo-yo', en: 'turn off' },
        { kind: 'teach', ko: '고장 났어요', roman: 'go-jang na-sseo-yo', en: "It's broken", note: 'Tell your manager this as soon as a machine stops working.' },
        { kind: 'choice', prompt: 'What does this mean?', ko: '기계를 꺼요', options: ['Turn off the machine', 'Turn on the machine', 'The machine is broken', 'Press the button'], answer: 0 },
        { kind: 'choice', prompt: 'The machine stopped working. What do you say?', options: ['고장 났어요', '괜찮아요', '켜요', '반갑습니다'], answer: 0 },
        { kind: 'choice', prompt: 'Listen. What did you hear?', speak: '전원을 켜요', listenOnly: true, options: ['Turn on the power', 'Turn off the power', 'Press the button', 'It is broken'], answer: 0 },
        { kind: 'match', prompt: 'Match the word to its meaning', pairs: [['기계', 'machine'], ['버튼', 'button'], ['켜요', 'turn on'], ['꺼요', 'turn off']] },
      ],
    },
    {
      id: 'safety',
      title: 'Safety',
      summary: 'Danger, be careful and the signs you will see',
      exercises: [
        { kind: 'teach', ko: '안전', roman: 'an-jeon', en: 'safety' },
        { kind: 'teach', ko: '위험', roman: 'wi-heom', en: 'danger', picture: '⚠️' },
        { kind: 'teach', ko: '조심하세요', roman: 'jo-sim-ha-se-yo', en: 'Be careful' },
        { kind: 'teach', ko: '금연', roman: 'geu-myeon', en: 'No smoking', picture: '🚭' },
        { kind: 'teach', ko: '출입 금지', roman: 'chu-rip geum-ji', en: 'No entry', picture: '⛔' },
        { kind: 'teach', ko: '안전모', roman: 'an-jeon-mo', en: 'safety helmet', picture: '⛑️' },
        { kind: 'teach', ko: '소화기', roman: 'so-hwa-gi', en: 'fire extinguisher', picture: '🧯' },
        { kind: 'choice', prompt: 'What does this sign mean?', ko: '금연', sign: true, options: ['No smoking', 'No entry', 'Danger', 'Exit'], answer: 0 },
        { kind: 'choice', prompt: 'What does this sign mean?', ko: '출입 금지', sign: true, options: ['No entry', 'No smoking', 'Be careful', 'Safety helmet'], answer: 0 },
        { kind: 'choice', prompt: 'There is a fire. What do you need?', options: ['🧯', '⛑️', '🔨', '🧤'], pictureOptions: true, answer: 0, explain: '소화기 (so-hwa-gi) is a fire extinguisher.' },
        { kind: 'match', prompt: 'Match the word to its meaning', pairs: [['위험', 'danger'], ['안전모', 'safety helmet'], ['조심하세요', 'be careful'], ['안전', 'safety']] },
      ],
    },
  ],
};
